import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Form, DataSource, TableSchema } from '@bi-studio/shared/lib';
import { getForm, getDataSources, getSchema } from '@bi-studio/shared/apis';
import { RootState } from '../index';

interface FormState {
    currentForm: Form | null;
    dataSources: DataSource[];
    tables: TableSchema[];
    isLoading: boolean;
    isLoadingDataSources: boolean;
    isLoadingTables: boolean;
    isSaving: boolean;
    error: string | null;
}

const initialState: FormState = {
    currentForm: null,
    dataSources: [],
    tables: [],
    isLoading: false,
    isLoadingDataSources: false,
    isLoadingTables: false,
    isSaving: false,
    error: null,
};

export const fetchFormDetails = createAsyncThunk(
    'form/fetchDetails',
    async ({ token, formId }: { token: string; formId: string }, { rejectWithValue }) => {
        try {
            const form = await getForm(token, formId);
            return form;
        } catch (error) {
            return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch form details');
        }
    }
);

export const fetchListDataSources = createAsyncThunk(
    'form/fetchDataSources',
    async (token: string, { rejectWithValue }) => {
        try {
            const dataSources = await getDataSources(token);
            return dataSources;
        } catch (error) {
            return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch data sources');
        }
    }
);

export const fetchTableSchema = createAsyncThunk(
    'form/fetchTableSchema',
    async ({ token, dataSourceId }: { token: string; dataSourceId: string }, { rejectWithValue }) => {
        try {
            const schema = await getSchema(token, dataSourceId);
            return schema || [];
        } catch (error) {
            return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch schema');
        }
    }
);

export const formSlice = createSlice({
    name: 'form',
    initialState,
    reducers: {
        resetFormState: (_state) => {
            return initialState;
        },
        setFormError: (state, action: PayloadAction<string | null>) => {
            state.error = action.payload;
        },
        setIsSaving: (state, action: PayloadAction<boolean>) => {
            state.isSaving = action.payload;
        },
        clearCurrentForm: (state) => {
            state.currentForm = null;
        }
    },
    extraReducers: (builder) => {
        builder
            // Fetch Form Details
            .addCase(fetchFormDetails.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchFormDetails.fulfilled, (state, action) => {
                state.isLoading = false;
                state.currentForm = action.payload;
            })
            .addCase(fetchFormDetails.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            })
            // Fetch Data Sources
            .addCase(fetchListDataSources.pending, (state) => {
                state.isLoadingDataSources = true;
            })
            .addCase(fetchListDataSources.fulfilled, (state, action) => {
                state.isLoadingDataSources = false;
                state.dataSources = action.payload;
            })
            .addCase(fetchListDataSources.rejected, (state, action) => {
                state.isLoadingDataSources = false;
                state.error = action.payload as string;
            })
            // Fetch Table Schema
            .addCase(fetchTableSchema.pending, (state) => {
                state.isLoadingTables = true;
            })
            .addCase(fetchTableSchema.fulfilled, (state, action) => {
                state.isLoadingTables = false;
                state.tables = action.payload;
            })
            .addCase(fetchTableSchema.rejected, (state, action) => {
                state.isLoadingTables = false;
                state.error = action.payload as string;
            });
    },
});

export const { resetFormState, setFormError, setIsSaving, clearCurrentForm } = formSlice.actions;

// Selectors
export const selectCurrentForm = (state: RootState) => state.form.currentForm;
export const selectFormSources = (state: RootState) => state.form.dataSources;
export const selectFormTables = (state: RootState) => state.form.tables;
export const selectFormLoading = (state: RootState) => state.form.isLoading;
export const selectFormSaving = (state: RootState) => state.form.isSaving;
export const selectFormError = (state: RootState) => state.form.error;
export const selectLoadingSources = (state: RootState) => state.form.isLoadingDataSources;
export const selectLoadingTables = (state: RootState) => state.form.isLoadingTables;

export default formSlice.reducer;
